import { useAuth } from '@dustin-web/microsite-components/src/auth'
import { useSiteContext } from '@dustin-web/microsite-components/src/hooks'
import * as React from 'react'
import { useEffect, useState } from 'react'
import AppContext from 'src/appContext'
import {
  GetFederatedBusinessCustomer,
  GetFederatedBusinessCustomerQuery,
  GetFederatedBusinessCustomerQueryVariables
} from 'src/graphql/generated/graphql-types'
import CustomRedirect from './customRedirect'
import InvalidCustomer from './registerBusiness/federatedAccess/InvalidCustomer'
import StandardRegister from './registerBusiness/standardRegister'
import StandardRegisterConsumer from './registerConsumer/standardRegister'
import StandardRegisterLegacy from './registerBusinessLegacy/standardRegister'
import StandardRegisterConsumerLegacy from './registerConsumerLegacy/standardRegister'
import FederatedAccessForm from './registerBusiness/federatedAccess/Form'
import { useGraphql } from '@dustin-web/microsite-data/src/hooks'
import Spinner from '@dustin-web/microsite-components/src/spinner'

enum State {
  loading,
  error,
  registerBusiness,
  registerConsumer,
  registerBusinessLegacy,
  registerConsumerLegacy,
  registerFederated
}

const Registration = ({ registrationState, setRegistrationState }: { registrationState: any, setRegistrationState: any }) => {
  const { userData, isAuthenticated } = useAuth()
  const { siteUrl, site } = useSiteContext()
  const [redirect, setRedirect] = useState<boolean>(false)
  const [state, setState] = useState<State>(isAuthenticated ? State.loading : State.registerBusiness)
  const [shouldBeFederated, setGetFederatedBusinessCustomer] = useState<boolean>(false)

  const { data } = useGraphql<GetFederatedBusinessCustomerQuery, GetFederatedBusinessCustomerQueryVariables>(
    GetFederatedBusinessCustomer,
    {
      context: {
        skip: !shouldBeFederated,
      },
      swrOptions: {
        onError: () => {
          setState(State.error)
        },
        onSuccess: () => {
          setState(State.registerFederated)
        },
      },
    }
  )

  useEffect(() => {
    if (state === State.loading && isAuthenticated && userData) {
      if (userData.contact) {
        setRedirect(true)
      }
      else if (userData.isFederated) {
        setGetFederatedBusinessCustomer(true)
      }
      else if (!userData.isFederated) {
        setState(State.registerBusiness)
      }
    }

  }, [isAuthenticated, userData, state])

  if (redirect) {
    return <CustomRedirect url={siteUrl ?? '/'} />
  }

  return (
    <AppContext.Consumer>
      {({
        firstName,
        lastName,
        fullName,
        email,
        isAuthenticated,
        preRegistrationToken,
        returnUrl,
      }) => {
        switch (state) {
          case State.registerBusiness:
            return (
              <StandardRegister
                firstName={firstName}
                lastName={lastName}
                email={email}
                isAuthenticated={isAuthenticated}
                preRegistrationToken={preRegistrationToken}
                returnUrl={returnUrl}
                cartId={null}
                acceptAccountTerms={false}
                companyName={""}
                disallowNewContacts={false}
                offersByEmail={site === 'DustinSweden' || site === 'DustinFinland' ? true : false}
                companyNameSetByOrgNumber={false}
                organizationNumber={""}
                password={""}
                registrationState={registrationState}
                setRegistrationState={setRegistrationState}
                additionalUserRequest={false}
              />
            )
          case State.registerBusinessLegacy:
            return (
              <StandardRegisterLegacy
                firstName={firstName}
                lastName={lastName}
                email={email}
                isAuthenticated={isAuthenticated}
                preRegistrationToken={preRegistrationToken}
                returnUrl={returnUrl}
                cartId={null}
                acceptAccountTerms={false}
                companyName={""}
                disallowNewContacts={false}
                offersByEmail={site === 'DustinSweden' || site === 'DustinFinland' ? true : false}
                companyNameSetByOrgNumber={false}
                organizationNumber={""}
                password={""}
              />
            )
          case State.registerConsumer:
            return (
              <StandardRegisterConsumer
                firstName={firstName}
                lastName={lastName}
                email={email}
                isAuthenticated={isAuthenticated}
                preRegistrationToken={preRegistrationToken}
                returnUrl={returnUrl}
                cartId={null}
                acceptAccountTerms={false}
                offersByEmail={false}
                password={""}
              />
            )
          case State.registerConsumerLegacy:
            return (
              <StandardRegisterConsumerLegacy
                firstName={firstName}
                lastName={lastName}
                email={email}
                isAuthenticated={isAuthenticated}
                preRegistrationToken={preRegistrationToken}
                returnUrl={returnUrl}
                cartId={null}
                acceptAccountTerms={false}
                offersByEmail={false}
                password={""}
              />
            )
          case State.registerFederated:
            return data?.federatedBusinessCustomer?.contactCanBeAdded ? (
              <FederatedAccessForm
                companyName={data?.federatedBusinessCustomer?.companyName}
                fullName={fullName}
                email={email}
                returnUrl={returnUrl}
                cartId={null}
              />
            ) : (
              <InvalidCustomer />
            )
          case State.loading:
            return <Spinner dustinSpinner />
          default:
            // TODO
            return <>Error</>
        }
      }}
    </AppContext.Consumer>
  )
}

export default Registration
